import React, { useRef, useState } from 'react';
import type { Client, Language, Order, Product } from '../types';
import type { Dict } from '../shared/i18n';
import { LOCALES, t as tr } from '../shared/i18n';
import { BRAND, type PlanLimits } from '../shared/config';
import { makeBackup, ordersToCsv, parseBackup } from '../shared/domain';
import { canUse, type Entitlement } from '../shared/entitlements';
import { deactivateLicense, getWebDeviceId } from '../services/security';
import { Card } from '../components/shared';

interface SettingsPageProps {
  products: Product[];
  clients: Client[];
  orders: Order[];
  lang: Language;
  t: Dict;
  limits: PlanLimits;
  entitlement: Entitlement | null;
  setLang: (lang: Language) => void;
  onRestore: (p: Product[], c: Client[], o: Order[]) => void;
  onActivate: () => void;
  onEntitlementChange: () => void;
}

const LANGS: { id: Language; label: string }[] = [
  { id: 'fr', label: 'Français' },
  { id: 'ar', label: 'العربية' },
  { id: 'en', label: 'English' },
];

/** Trigger a download without leaving the page or needing a server. */
function download(filename: string, contents: string, mime: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SettingsPage: React.FC<SettingsPageProps> = ({
  products, clients, orders, lang, t, limits, entitlement,
  setLang, onRestore, onActivate, onEntitlementChange,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [deviceId, setDeviceId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  React.useEffect(() => { void getWebDeviceId().then((id) => setDeviceId(id.slice(0, 8))); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const dateFmt = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang], {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  const exportCsv = () => {
    if (orders.length === 0) { setMessage(t.noOrders); return; }
    download(`quickdelivery-${today}.csv`, ordersToCsv(orders, t.currency), 'text/csv;charset=utf-8');
  };

  const backup = () => {
    download(
      `quickdelivery-backup-${today}.json`,
      JSON.stringify(makeBackup(products, clients, orders)),
      'application/json',
    );
    setMessage(t.backupDone);
  };

  const restore = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    e.target.value = '';
    if (!file) return;
    if (!confirm(t.restoreWarning)) return;

    try {
      const data = parseBackup(await file.text());
      onRestore(data.products, data.clients, data.orders);
      setMessage(t.restoreDone);
    } catch {
      setMessage(t.empty);
    }
  };

  const claims = entitlement?.claims;

  return (
    <div className="space-y-4 qd-enter">
      <Card className="space-y-3">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.activation}</p>

        {entitlement?.status === 'pro' && claims ? (
          <>
            <div className="flex justify-between items-center">
              <span className="font-extrabold text-slate-900">{t.licenseActive}</span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#006233]" aria-hidden="true" />
            </div>
            <p className="text-xs text-slate-500">
              {claims.plan === 'lifetime' ? t.licenseLifetime : tr(t, 'licenseUntil', { d: dateFmt.format(claims.exp) })}
            </p>
            {claims.ref && <p className="text-[11px] text-slate-400 font-mono">{claims.ref}</p>}
            <button
              onClick={async () => {
                if (!confirm(t.restoreWarning)) return;
                await deactivateLicense();
                onEntitlementChange();
              }}
              className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-extrabold text-sm"
            >
              {t.deactivate}
            </button>
          </>
        ) : (
          <>
            <p className="font-extrabold text-slate-900">
              {entitlement?.status === 'trial'
                ? tr(t, 'trialBanner', { n: entitlement.trialDaysLeft })
                : t.freePlan}
            </p>
            <p className="text-xs text-slate-500">{t.upgradeBlurb}</p>
            <a
              href={`${BRAND.siteUrl}/#pricing`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-[#C1272D] text-white py-3 rounded-xl font-extrabold text-sm"
            >
              {t.buyLicense}
            </a>
            <button
              onClick={onActivate}
              className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-extrabold text-sm"
            >
              {t.haveKey}
            </button>
          </>
        )}

        <div className="flex justify-between items-center pt-3 border-t border-slate-100 text-[11px]">
          <span className="text-slate-400">{t.deviceId}</span>
          <span className="font-mono font-bold text-slate-600">{deviceId}</span>
        </div>
      </Card>

      <Card className="space-y-3">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.language}</p>
        <div className="grid grid-cols-3 gap-2">
          {LANGS.map((l) => (
            <button
              key={l.id}
              onClick={() => setLang(l.id)}
              aria-pressed={lang === l.id}
              className={`py-3 rounded-xl text-xs font-extrabold border ${
                lang === l.id ? 'bg-[#C1272D] text-white border-[#C1272D]' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Card>

      <Card className="space-y-3">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.backup}</p>
        <p className="text-xs text-slate-500">{t.dataStoredLocally}</p>

        <button
          onClick={canUse(limits, 'export') ? exportCsv : onActivate}
          className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-3 rounded-xl font-extrabold text-sm"
        >
          {canUse(limits, 'export') ? t.exportCsv : `🔒 ${t.exportCsv}`}
        </button>
        <button
          onClick={canUse(limits, 'backup') ? backup : onActivate}
          className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-3 rounded-xl font-extrabold text-sm"
        >
          {canUse(limits, 'backup') ? t.backup : `🔒 ${t.backup}`}
        </button>
        {/* Restore is available on every plan: locking someone out of their own
            backup would be indefensible. */}
        <button
          onClick={() => fileRef.current?.click()}
          className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-3 rounded-xl font-extrabold text-sm"
        >
          {t.restoreBackup}
        </button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={restore} />

        {message && <p role="status" className="text-xs font-bold text-[#006233]">{message}</p>}

        <div className="flex justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-100">
          <span>{t.products} · {t.clients} · {t.orderHistory}</span>
          <span>{products.length} · {clients.length} · {orders.length}</span>
        </div>
      </Card>

      <Card className="space-y-2">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{BRAND.name}</p>
        <p className="text-xs text-slate-400">v2.0.0 · {BRAND.vendor}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[11px] font-bold">
          <a href={BRAND.siteUrl} target="_blank" rel="noopener noreferrer" className="text-[#C1272D]">{BRAND.domain}</a>
          <a href={`${BRAND.siteUrl}/privacy`} target="_blank" rel="noopener noreferrer" className="text-[#C1272D]">Privacy</a>
          <a href={`${BRAND.siteUrl}/terms`} target="_blank" rel="noopener noreferrer" className="text-[#C1272D]">Terms</a>
          <a href={`mailto:${BRAND.supportEmail}`} className="text-[#C1272D]">{BRAND.supportEmail}</a>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPage;
